import { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "./styles/home.css";

const homeStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://yourenergyguru.com/#org",
      name: "Your Energy Guru",
      url: "https://yourenergyguru.com/",
      description:
        "Your Energy Guru helps homeowners compare solar quotes from vetted local installers and understand incentives, payback, and net metering.",
    },
    {
      "@type": "WebSite",
      "@id": "https://yourenergyguru.com/#website",
      url: "https://yourenergyguru.com/",
      name: "Your Energy Guru",
      publisher: { "@id": "https://yourenergyguru.com/#org" },
    },
  ],
};

// Lazy load components
const TrustSection = lazy(() => import("@/components/TrustSection"));
const FeaturedSection = lazy(() => import("@/components/FeaturedSection"));
const FAQPreview = lazy(() => import("@/components/FAQPreview"));

export default function Home() {
  return (
    <div className="home-container">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeStructuredData) }}
      />
      <div className="min-h-screen">
        <main className="flex-1">
          {/* ───────────── Hero ───────────── */}
          <section className="home-hero">
            <div className="hero-bg">
              <img
                src="/images-webp/home-hero-bright.webp"
                width={2000}
                height={1333}
                alt="Sunlit home with a large rooftop solar array under a bright blue sky"
                loading="eager"
                fetchPriority="high"
              />
            </div>

            <div className="hero-scrim" />

            <div className="hero-content">
              <h1 className="hero-title">See How Much You Could Save With Solar</h1>
              <p className="hero-subtitle">
                Get a free, no-obligation quote from vetted local installers — in
                about two minutes.
              </p>

              <div className="hero-cta-group">
                <Link to="/quote" className="hero-cta primary">
                  Get My Free Quote
                  <ArrowRight className="cta-icon" />
                </Link>
                <Link to="/calculator" className="hero-cta secondary">
                  Calculate My Savings
                </Link>
              </div>

              <p className="hero-trust">
                100% free · No obligation · Takes about 2 minutes
              </p>
            </div>
          </section>

          {/* ───────────── How It Works ───────────── */}
          <section className="home-how">
            <div className="how-container">
              <h2 className="how-title">How It Works</h2>
              <p className="how-subtitle">
                Three simple steps — no pressure, no cost.
              </p>
              <div className="how-grid">
                <div className="how-step">
                  <div className="how-step-num">1</div>
                  <h3>Tell us about your home</h3>
                  <p>
                    Answer a few quick questions about your roof and energy bill.
                    It takes about two minutes.
                  </p>
                </div>
                <div className="how-step">
                  <div className="how-step-num">2</div>
                  <h3>We match you with vetted pros</h3>
                  <p>
                    We connect you with trusted local installers — no sales
                    pressure and no spam.
                  </p>
                </div>
                <div className="how-step">
                  <div className="how-step-num">3</div>
                  <h3>Compare quotes &amp; save</h3>
                  <p>
                    Review your options side by side and choose what's right for
                    you — or walk away. It's your call.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Lazy loaded sections */}
          <Suspense fallback={null}>
            <TrustSection />
          </Suspense>

          <Suspense fallback={null}>
            <FeaturedSection />
          </Suspense>

          <Suspense fallback={null}>
            <FAQPreview />
          </Suspense>
        </main>
      </div>

      {/* Persistent quote CTA on mobile */}
      <Link to="/quote" className="sticky-cta">
        Get My Free Quote →
      </Link>
    </div>
  );
}
